import st from "@/styles/LoginPage.module.scss";
import React, {ReactNode, useEffect, useMemo, useRef, useState} from "react";
import Accordion from "@/ui/Accordion.tsx";
import Countdown, {CountdownRenderProps} from "react-countdown";
import Button from "@/ui/Button.tsx";
import ChevroneIcon from "@/svg/ChevronIocn.tsx";
import LoginLoader from "@/ui/LoginLoader.tsx";
import LoginData from "@/types_and_interfaces/login_data.ts";
import DashboardClass from "@/utils/dashboard_class.ts";
import PhoneIcon from "@/svg/PhoneIcon.tsx";
import PhoneNumberInput from "@/ui/PhoneNumberInput.tsx";
import CommonClass from "@/utils/common.ts";
import EmailIcon from "@/svg/EmailIcon.tsx";
import {TextField} from "@mui/material";
import {textfield_sx} from "@/utils/mui_sx.ts";
import {strings} from "@/utils/strings.ts";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {getDeviceUUID} from "@/utils/export_functions.ts";
import useCommon from "@/hooks/common.ts";
import useAuth from "@/hooks/auth.ts";
import {useAtom} from "jotai";
import {login_data} from "@/atoms/profile.ts";
import {refresh_button_show} from "@/atoms/common.ts";
import Images from "@/utils/images.ts";
import {codeType} from "@/types_and_interfaces/types.ts";
import HaveLoggedInterface from "@/types_and_interfaces/have_logged_interface.ts";
import ReCAPTCHA from "react-google-recaptcha";
import {CheckPhoneData} from "@/types_and_interfaces/check_phone_data_interface.ts";

interface TypeButton {
    icon:ReactNode
    type: codeType | string
    label: string
    visible: boolean
    no_chevrone: boolean
    custom_function?():void
    elems?:ReactNode
    accordion: boolean
}

interface ErrInterface { phone: string, email: string }

interface MainLoginFormProps {
    checkPhoneData?: CheckPhoneData | null;
}

const MainLoginForm:React.FC<MainLoginFormProps> = ({ checkPhoneData }) => {
    // When checkPhoneData is provided, we're showing alternative login options (e.g., from PIN step)
    const isAlternativeMode = !!checkPhoneData;

    const pastUser = localStorage.getItem(CommonClass.local_storage_fields.have_logged_data)
    const past_data: HaveLoggedInterface = pastUser !== null ? JSON.parse(pastUser) : {}
    const deviceUid= getDeviceUUID() || ""
    const past_registered = typeof past_data === "object" && Object.keys(past_data).length && typeof deviceUid === "string" && deviceUid.length && past_data.has_pwd

    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const common = useCommon()
    const auth_settings = CommonClass.general_settings.auth
    const auth = useAuth();
    const initLocalErr = {phone:"", email:""}
    const localErr = useRef<ErrInterface>(initLocalErr)
    const [loginData, setLoginData] = useAtom<string>(login_data)
    const [show_refresh] = useAtom(refresh_button_show)

    const [countEnd, setCountEnd] = useState(true)
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState('');
    const [codeType, setCodeType] = useState<codeType>(auth_settings?.phone?.is_enabled ? "phone" : "email")
    const [loading, setLoading] = useState<boolean>(false)
    const [errors, setError] = useState<ErrInterface>({phone:"", email:""})

    const captchaRef = useRef<string>("");
    const autoLoginPhoneRef = useRef<string>("");
    const mpass_image = <img src={Images.common.mpass_small} alt="mpass_small" width={32} height={32}/>
    const lg_data: LoginData = Boolean(loginData) ? JSON.parse(loginData) : {}

    const countDownCompleteHandler = () => {
        localStorage.setItem(CommonClass.local_storage_fields.login_data, JSON.stringify({
            type: lg_data.type,
            interval: undefined,
            value: lg_data.value as string
        }))

        setCountEnd(true)
    }


    const handlePhoneNumber = (x: string) => {
        setPhoneNumber(x)
    }

    const autoLoginFromLoanData = async (phone: string, captchaToken?: string) => {
        let hasPasscode: boolean = false;
        try {
            setLoading(true)
            localErr.current = initLocalErr
            setError(localErr.current)

            const res = await auth.checkUserPhoneBeforeLogin(phone, phone, captchaToken);

            localErr.current = { ...localErr.current, phone: res.error };
            setError(localErr.current)

            // If backend says "you_are_a_bot", store phone and wait for captcha
            if (res.error === t("you_are_a_bot")) {
                autoLoginPhoneRef.current = phone
                setLoading(false)
                return
            }

            if (res.error) {
                setLoading(false)
                return
            }

            hasPasscode = res.data?.has_passcode ?? false;

            if (!hasPasscode) {
                const otp_res = await auth.loginPhoneSt1(phone);
                localErr.current = { ...localErr.current, phone: otp_res.error };
                if (otp_res.error) {
                    setError(localErr.current)
                    setLoading(false)
                    return
                }
            }

            let currentDate = new Date();
            if (!hasPasscode) currentDate.setSeconds(currentDate.getSeconds() + 30);

            const loginDataObj: LoginData = {
                type: "phone",
                interval: currentDate,
                value: phone
            }

            setLoginData(JSON.stringify(loginDataObj))

            common.firebase_init(i18n.language)

        } finally {
            setLoading(false)
        }

        if (show_refresh) {
            window.location.href = `${DashboardClass.login_routes.LoginStep2}&isPin=${String(hasPasscode)}`;
        } else {
            navigate(`${DashboardClass.login_routes.LoginStep2}&isPin=${String(hasPasscode)}`);
        }
    }

    // Effect - check for loan_form_data and auto-login when recaptcha is ready
    // Skip in alternative mode (e.g., from WritePinSecondStep)
    useEffect(() => {
        if (isAlternativeMode) return
        if (!auth.isRecaptchaReady) return

        const loan_form_data = sessionStorage.getItem(CommonClass.local_storage_fields.apply_credit_form_body)

        if(loan_form_data !== null) {
            let d = JSON.parse(loan_form_data)

            if(d.hasOwnProperty("phone") && typeof d.phone === "string" && d.phone.length) {
                setPhoneNumber(d.phone)

                if (CommonClass.phoneNumberValidate(d.phone)) {
                    autoLoginFromLoanData(d.phone)
                }
            }
        }
    }, [auth.isRecaptchaReady, isAlternativeMode]);

    const handleCaptchaChange = (token: string | null) => {
        const t = token || "";
        captchaRef.current = t;

        if (autoLoginPhoneRef.current.length > 0 && t.length > 0) {
            autoLoginFromLoanData(autoLoginPhoneRef.current, t)
            autoLoginPhoneRef.current = ""
        }
    }

    // Skip auto-fill from loginData in alternative mode
    useEffect(() => {
        if (isAlternativeMode) return

        if (lg_data.hasOwnProperty("interval") && lg_data.interval !== undefined) {
            setCountEnd(false)
        }

        if (Boolean(lg_data.type) && lg_data.type.length && Boolean(lg_data.value) && lg_data.value.length) {
            setCodeType(lg_data.type)

            if (CommonClass.isEmailValid(lg_data.value)) {
                setEmail(lg_data.value)
            } else {
                setPhoneNumber(lg_data.value)
            }
        }

    }, [loginData, isAlternativeMode]);


    const submitForm = async (e: any) => {
        e.preventDefault()
        let isPin: boolean = false;
        try {
            setLoading(true)
            let form = new FormData(e.target)
            localErr.current = initLocalErr
            setError(localErr.current)
            let field = form.get(codeType)

            if (!field) {
                localErr.current = {...localErr.current, ...(codeType === "email" ? {email: t("complete_field_err")} : {phone: t("complete_field_err")} )}
            }

            if (countEnd || lg_data?.type !== codeType) {
                if (codeType === "email" && typeof field === "string") {

                    await auth.loginEmailSt1(field).then((x) => {

                        localErr.current = {...localErr.current, ...{email: x.error}}

                    })

                    isPin = false;
                }

                if (codeType === "phone" && typeof field === "string") {
                    let res;

                    res = await auth.checkUserPhoneBeforeLogin(field, phoneNumber);

                    localErr.current = { ...localErr.current, phone: res.error };
                    setError(localErr.current)

                    if (res.error === t("you_are_a_bot")) {
                        setError(localErr.current);

                        setLoading(false);

                        //
                        await new Promise<void>((resolve) => {
                            const stop = setInterval(() => {
                                if (captchaRef.current.length > 0) {
                                    clearInterval(stop);
                                    resolve();
                                }
                            }, 100);
                        });


                        //Once captchaToken is available → retry WITH TOKEN
                        res = await auth.checkUserPhoneBeforeLogin(field, phoneNumber, captchaRef.current);

                        localErr.current = { ...localErr.current, phone: res.error };

                        // If still error → stop completely
                        if (res.error) {
                            setError(localErr.current);
                            return;
                        }
                    }

                    if (localErr.current.phone.length) return;

                    isPin = res.data?.has_passcode ?? false;

                    if (!isPin) {
                        const otp_res = await auth.loginPhoneSt1(phoneNumber);
                        localErr.current = { ...localErr.current, phone: otp_res.error };
                    }
                }
            }

            if(localErr.current.phone.length || localErr.current.email.length) {
                setError(localErr.current)
                return
            }

            // Update apply_credit_form_body phone if in alternative mode and using phone
            if (isAlternativeMode && codeType === "phone") {
                const storedFormBody = sessionStorage.getItem(CommonClass.local_storage_fields.apply_credit_form_body)
                if (storedFormBody) {
                    try {
                        const formBodyData = JSON.parse(storedFormBody)
                        formBodyData.phone = phoneNumber
                        sessionStorage.setItem(CommonClass.local_storage_fields.apply_credit_form_body, JSON.stringify(formBodyData))
                    } catch {
                        // Ignore parse errors
                    }
                }
            }

            let currentDate = new Date();
            if(!isPin) currentDate.setSeconds(currentDate.getSeconds() + 30);

            let data: LoginData = {
                type: codeType,
                interval: currentDate,
                value: field as string
            }

            setLoginData(JSON.stringify(data))

            common.firebase_init(i18n.language)

        } finally {
            setLoading(false)
        }

        if(show_refresh){
            window.location.href = `${DashboardClass.login_routes.LoginStep2}&isPin=${String(isPin)}`;
        } else if (!errors.phone.length || !errors.email.length) {
            navigate(`${DashboardClass.login_routes.LoginStep2}&isPin=${String(isPin)}`);
        }
    }

    const type_btns: Array<TypeButton> = [
        {
            icon: <PhoneIcon/>,
            type: "phone",
            label: t("phone_number"),
            visible: auth_settings?.phone?.is_enabled,
            no_chevrone: false,
            elems: <>
                <p>
                    {t("number_code_info_text_simplified")}
                </p>

                <div className={st.field_container}>
                    <PhoneNumberInput
                        error={Boolean(errors.phone.length) && errors.phone !== t('you_are_a_bot')}
                        defaultValue={(phoneNumber.length && CommonClass.phoneNumberValidate(phoneNumber)) ? phoneNumber : ""}
                        setNumberState={handlePhoneNumber}
                    />

                    {(!!errors.phone.length && errors.phone !== t('you_are_a_bot')) && (<p className={st.err_text}>{errors.phone}</p>)}

                </div>

            </>,
            accordion: true

        },

        {
            icon: <EmailIcon/>,
            type: "email",
            label: t("email_address"),
            visible: isAlternativeMode
                ? auth_settings?.email?.is_enabled && !!checkPhoneData?.has_email
                : !!past_registered && !!past_data?.has_email,
            no_chevrone: false,
            elems: <>
                <p>
                    {t("email_code_info_text")}
                </p>

                <div className={st.field_container}>
                    <TextField
                        label="Email"
                        name={"email"}
                        error={Boolean(errors.email.length)}
                        sx={textfield_sx}
                        color="success"
                        placeholder={"email.address@email.com"}
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value)
                        }}
                    />
                    {!!errors.email.length && (<p className={st.err_text}>{errors.email}</p>)}

                </div>

            </>,
            accordion: true
        },

        {
            icon: mpass_image,
            type: "mpass",
            label: strings["mpass"],
            visible: isAlternativeMode
                ? auth_settings?.mpass?.individual_is_enabled && !!checkPhoneData?.has_mpass
                : auth_settings?.mpass?.individual_is_enabled,
            no_chevrone: false,
            accordion: false
        },

    ]

    const all_disabled = useMemo(
        () => !type_btns.some((x: TypeButton) => x.visible),
        [type_btns]
    )

    return (
        <div className={st.log_vars}>

            <form onSubmit={submitForm} action="send-code">
                {past_registered ? (
                    <span className={st.inf}>
                                {t("alt_login_methods")}
                            </span>
                ) : null}

                {all_disabled ? (
                    <span>
                                {t("error_message")}
                            </span>
                ) : type_btns.map((tb: TypeButton) => {

                    const {
                        icon,
                        type,
                        label,
                        visible,
                        no_chevrone,
                        custom_function,
                        elems,
                        accordion
                    } = tb

                    return visible && (
                        <React.Fragment key={`type_${type}`}>

                            {accordion ? (
                                <Accordion
                                    no_chevrone={no_chevrone}
                                    open={codeType === type}
                                    icon={icon}
                                    label={label}
                                    onClick={() => {
                                        if (typeof custom_function === "function") {
                                            custom_function()
                                            return
                                        }
                                        setCodeType(type as codeType)
                                    }}
                                >
                                    <div className={st.form_core}>
                                        {elems && elems}

                                        {(!countEnd && lg_data.type === codeType) && (
                                            <div className={st.countdown}>
                                                {t("resend_in")}

                                                <Countdown
                                                    date={lg_data?.interval}
                                                    renderer={(args) => renderer({...args})}
                                                    intervalDelay={1000}
                                                    zeroPadTime={2}
                                                    onComplete={countDownCompleteHandler}
                                                />
                                            </div>
                                        )}

                                        {errors.phone === t("you_are_a_bot") && (
                                            <ReCAPTCHA
                                                sitekey={CommonClass.general_settings.recaptcha_v2.public_key}
                                                onChange={handleCaptchaChange}
                                            />
                                        )}

                                        <Button
                                            animated
                                            disabled={
                                                (codeType === "email" && !Boolean(email.length) && !countEnd) ||
                                                (codeType === "phone" && phoneNumber.length !== 10) ||
                                                (!countEnd && lg_data.type === codeType)
                                            }
                                            type={"submit"}
                                        >

                                            <span>
                                                {tb.type == "phone" ? t("continue1") : t("send_code")}
                                            </span>

                                        </Button>
                                    </div>

                                </Accordion>
                            ) : (
                                <Button
                                    type={"button"}
                                    className={st.log_variant_button}
                                    onClick={() => {
                                        common.firebase_init(i18n.language)
                                        auth.authMpass("individual")
                                    }}
                                >

                                    <div className={st.left}>
                                        {icon}
                                        {label}
                                    </div>

                                    <div className={st.right}>
                                        <ChevroneIcon/>
                                    </div>

                                </Button>
                            )}

                        </React.Fragment>
                    )
                })}

                {loading && <LoginLoader/>}

            </form>

            {auth_settings?.mpass?.company_is_enabled && (
                <Button
                    dark
                    onClick={() => {
                        auth.authMpass("company")
                    }}
                    className={st.mpass}
                >
                    <div className={st.left}>
                        {mpass_image}
                        {t("connect_as_jp")}
                    </div>

                    <div className={st.right}>
                        <ChevroneIcon/>
                    </div>
                </Button>
            )}
        </div>
    )
}

const renderer = (args: CountdownRenderProps) => {
    const {minutes, seconds} = args;
    const padValue = (value: number) => (value < 10 ? `0${value}` : `${value}`);

    return (
        <div>
            {padValue(minutes) || "00"} :
            {" " + padValue(seconds) || " 00"}
        </div>
    );

};

export default MainLoginForm